// src/components/resources/tools/generators/CommentThread/ThreadCommentEditor.tsx

'use client'

import { useState, type FC } from 'react'
import { ChevronDown, ChevronRight, Plus, Reply, Trash2 } from 'lucide-react'
import { clsx } from 'clsx'
import {
  addReplyById,
  avatarInitial,
  countComments,
  makeComment,
  MAX_THREAD_COMMENTS,
  normalizeHandle,
  removeCommentById,
  updateCommentById,
  type ThreadComment,
  type ThreadEditorCapabilities,
} from './threadModel'

const TEXT_INPUT_CLASS =
  'rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white'

const ICON_BUTTON_CLASS =
  'flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800'

/** Small aria-pressed pill used for the per-comment badge/state toggles. */
const TogglePill: FC<{
  pressed: boolean
  label: string
  onToggle: () => void
  ariaLabel?: string
}> = ({ pressed, label, onToggle, ariaLabel }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={pressed}
    aria-label={ariaLabel}
    className={clsx(
      'rounded-full border px-2 py-0.5 text-[11px] font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
      pressed
        ? 'border-slate-900 bg-slate-900 text-white dark:border-sky-500 dark:bg-sky-500'
        : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
    )}
  >
    {label}
  </button>
)

export interface ThreadCommentEditorProps {
  comments: ThreadComment[]
  onCommentsChange: (next: ThreadComment[]) => void
  /** Which toggles/limits this surface exposes (per platform / Reddit mode). */
  capabilities: ThreadEditorCapabilities
  /**
   * The post author's handle. When a comment's username is edited to match it
   * (normalized), the author badge auto-enables — the common case for staging
   * a creator reply — while the badge pill stays a manual override either way.
   */
  postAuthor?: string
  /** Called after any structural edit for analytics (add/remove/reply). */
  onStructureChange?: (interaction: 'comment_added' | 'reply_added' | 'comment_removed') => void
}

/**
 * The shared thread editor: every comment AND every nested reply is fully
 * editable in place — text, handle, likes, timestamp, badges, liked state —
 * and can be removed or replied to (up to the surface's `maxDepth`). This is
 * the editor contract the tool copy advertises, so nothing here may be
 * display-only.
 *
 * UX model: each comment is a collapsible card. The header names the comment
 * (avatar initial + handle + a text snippet when collapsed) and carries the
 * three per-comment actions — expand/collapse (`aria-expanded`), Reply,
 * Remove — so edit affordances stay discoverable even when a long thread is
 * collapsed to headers. Badge/state toggles are compact `aria-pressed` pills
 * grouped beside the likes input.
 */
export const ThreadCommentEditor: FC<ThreadCommentEditorProps> = ({
  comments,
  onCommentsChange,
  capabilities,
  postAuthor,
  onStructureChange,
}) => {
  const total = countComments(comments)
  const atCapacity = total >= MAX_THREAD_COMMENTS
  // Cards are expanded unless explicitly collapsed, so a fresh comment/reply
  // opens ready to edit and a long thread can be tamed card by card.
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(new Set())
  // In-flight text of a likes input, keyed by comment id. A controlled numeric
  // input that eagerly coerces kills intermediate states — `parseInt('-')` is
  // NaN, so typing a negative Reddit score died on the very first `-`
  // keystroke (the coerced 0 overwrote it). The draft holds what the user has
  // typed; the model updates only when it parses, and blur clears the draft so
  // the display re-normalizes to the model value.
  const [likesDrafts, setLikesDrafts] = useState<Record<string, string>>({})

  const toggleCollapsed = (id: string): void => {
    setCollapsedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const patch = (id: string, changes: Partial<Omit<ThreadComment, 'id' | 'replies'>>): void =>
    onCommentsChange(updateCommentById(comments, id, changes))

  /**
   * Username edits keep the badge/handle invariant in BOTH directions: a
   * handle edited to match the creator auto-enables the author badge, and a
   * badged comment edited AWAY from the creator handle auto-clears it —
   * otherwise a customer handle could export with an Author/OP badge until
   * the pill was manually toggled off. The pill remains the manual override
   * either way (and no auto-clear happens when no creator handle is set,
   * since there is nothing to mismatch against).
   *
   * The invariant applies at the MODEL level, deliberately NOT gated on
   * `capabilities.authorBadge`: `byAuthor` means "written by the post author"
   * regardless of whether the CURRENT platform renders a badge. X hides the
   * badge (and its pill), so gating on the capability let an author-badged
   * comment be renamed on X while silently keeping `byAuthor: true` — the
   * customer handle then exported with an Author/Creator badge (and followed
   * creator renames) after switching back to Instagram/YouTube/TikTok, with
   * no pill on X to clear it (`r3564364628`).
   */
  const patchUsername = (comment: ThreadComment, username: string): void => {
    const creatorHandle = typeof postAuthor === 'string' ? normalizeHandle(postAuthor) : ''
    if (creatorHandle.length === 0) {
      patch(comment.id, { username })
      return
    }
    if (normalizeHandle(username) === creatorHandle) {
      patch(comment.id, { username, byAuthor: true })
    } else if (comment.byAuthor) {
      patch(comment.id, { username, byAuthor: false })
    } else {
      patch(comment.id, { username })
    }
  }

  const addReply = (parentId: string): void => {
    onCommentsChange(addReplyById(comments, parentId, makeComment({ timestamp: '1h' })))
    onStructureChange?.('reply_added')
  }

  const remove = (id: string): void => {
    onCommentsChange(removeCommentById(comments, id))
    onStructureChange?.('comment_removed')
  }

  const addTopLevel = (): void => {
    onCommentsChange([...comments, makeComment({ timestamp: '1h' })])
    onStructureChange?.('comment_added')
  }

  // Render function (NOT a nested component): defining a component type inside
  // render would remount the whole subtree on every parent re-render, blowing
  // away input focus mid-keystroke. A plain function call keeps these elements
  // in the same tree position with stable keys, so controlled inputs retain DOM
  // identity while the user types.
  const renderCommentFields = (comment: ThreadComment, depth: number, label: string): JSX.Element => {
    // Accessible-name suffix so "Remove comment" is unambiguous in a long thread.
    const who = normalizeHandle(comment.username) || 'new comment'
    const isCollapsed = collapsedIds.has(comment.id)
    const bodyId = `thread-comment-body-${comment.id}`
    const snippet = comment.text.length > 34 ? `${comment.text.slice(0, 34)}…` : comment.text
    return (
      <div
        className={clsx(
          'rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900',
          depth > 0 && 'ml-3 border-l-2 border-l-slate-300 dark:border-l-slate-600',
        )}
      >
        {/* Header: identity + the three per-comment actions. */}
        <div className="flex items-center gap-1 p-2">
          <button
            type="button"
            onClick={() => toggleCollapsed(comment.id)}
            aria-expanded={!isCollapsed}
            aria-controls={bodyId}
            className="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-0.5 text-left transition hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            {isCollapsed ? (
              <ChevronRight aria-hidden="true" className="h-3 w-3 flex-shrink-0 text-slate-400" />
            ) : (
              <ChevronDown aria-hidden="true" className="h-3 w-3 flex-shrink-0 text-slate-400" />
            )}
            <span
              aria-hidden="true"
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-500 to-slate-700 text-[10px] font-bold text-white"
            >
              {avatarInitial(comment.username)}
            </span>
            <span className="min-w-0 truncate text-[11px] font-semibold text-slate-700 dark:text-slate-200">
              {label}
              {who !== 'new comment' && <span className="font-normal text-slate-500 dark:text-slate-400"> · {who}</span>}
              {isCollapsed && snippet && (
                <span className="font-normal text-slate-400 dark:text-slate-500"> — {snippet}</span>
              )}
            </span>
          </button>
          {depth < capabilities.maxDepth && (
            <button
              type="button"
              onClick={() => addReply(comment.id)}
              disabled={atCapacity}
              aria-label={`Add reply to ${label.toLowerCase()} by ${who}`}
              className={ICON_BUTTON_CLASS}
            >
              <Reply aria-hidden="true" className="h-3 w-3" />
              Reply
            </button>
          )}
          <button
            type="button"
            onClick={() => remove(comment.id)}
            aria-label={`Remove ${label.toLowerCase()} by ${who}`}
            className={clsx(ICON_BUTTON_CLASS, 'hover:text-red-600')}
          >
            <Trash2 aria-hidden="true" className="h-3 w-3" />
          </button>
        </div>

        {/* Body: fields + toggle pills. */}
        {!isCollapsed && (
          <div id={bodyId} className="space-y-2 px-2 pb-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <label className="flex flex-col gap-0.5">
                <span className="font-medium text-slate-700 dark:text-slate-200">Username</span>
                <input
                  type="text"
                  value={comment.username}
                  onChange={e => patchUsername(comment, e.target.value)}
                  placeholder="@username"
                  maxLength={50}
                  className={TEXT_INPUT_CLASS}
                />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="font-medium text-slate-700 dark:text-slate-200">Timestamp</span>
                <input
                  type="text"
                  value={comment.timestamp}
                  onChange={e => patch(comment.id, { timestamp: e.target.value })}
                  placeholder="2h"
                  maxLength={20}
                  className={TEXT_INPUT_CLASS}
                />
              </label>
              <label className="col-span-2 flex flex-col gap-0.5">
                <span className="font-medium text-slate-700 dark:text-slate-200">Comment</span>
                <textarea
                  value={comment.text}
                  onChange={e => patch(comment.id, { text: e.target.value })}
                  maxLength={2200}
                  className={clsx(TEXT_INPUT_CLASS, 'min-h-[48px] leading-relaxed')}
                />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="font-medium text-slate-700 dark:text-slate-200">{capabilities.likesLabel}</span>
                <input
                  // Text + numeric inputMode (not type="number"): the browser
                  // sanitizes an in-progress "-" out of a number input's value,
                  // which made negative Reddit scores un-typeable.
                  type="text"
                  inputMode="numeric"
                  pattern={capabilities.allowNegativeLikes ? '-?[0-9]*' : '[0-9]*'}
                  value={likesDrafts[comment.id] ?? String(comment.likes)}
                  onChange={e => {
                    const raw = e.target.value
                    setLikesDrafts(drafts => ({ ...drafts, [comment.id]: raw }))
                    const parsed = Number.parseInt(raw, 10)
                    if (Number.isNaN(parsed)) return // intermediate '', '-' — keep the draft, model unchanged
                    // Clamp typed negatives on the social platforms, where a
                    // negative like count is impossible; Reddit scores may go
                    // below zero.
                    patch(comment.id, { likes: capabilities.allowNegativeLikes ? parsed : Math.max(0, parsed) })
                  }}
                  onBlur={() =>
                    setLikesDrafts(drafts => {
                      if (!(comment.id in drafts)) return drafts
                      const { [comment.id]: _cleared, ...rest } = drafts
                      return rest
                    })
                  }
                  className={TEXT_INPUT_CLASS}
                />
              </label>
              <div className="flex flex-wrap content-end items-end gap-1 pb-0.5">
                {capabilities.likedToggle && (
                  <TogglePill
                    pressed={comment.liked}
                    label="Liked"
                    ariaLabel={`Mark comment by ${who} as liked by the viewer`}
                    onToggle={() => patch(comment.id, { liked: !comment.liked })}
                  />
                )}
                {capabilities.creatorHeart && (
                  <TogglePill
                    pressed={comment.creatorHearted}
                    label="Creator ♥"
                    ariaLabel={`Toggle creator heart on comment by ${who}`}
                    onToggle={() => patch(comment.id, { creatorHearted: !comment.creatorHearted })}
                  />
                )}
                {capabilities.verified && (
                  <TogglePill
                    pressed={comment.verified}
                    label="Verified"
                    ariaLabel={`Toggle verified badge on comment by ${who}`}
                    onToggle={() => patch(comment.id, { verified: !comment.verified })}
                  />
                )}
                {capabilities.authorBadge && (
                  <TogglePill
                    pressed={comment.byAuthor}
                    label={capabilities.authorBadgeLabel}
                    ariaLabel={`Toggle ${capabilities.authorBadgeLabel} badge on comment by ${who}`}
                    // The badge/handle invariant must hold in EVERY path:
                    // `byAuthor` means "written by the post author", so
                    // toggling it ON also adopts the creator handle (the
                    // username field stays editable afterwards, same as the
                    // rename-follow path). Toggling OFF keeps the typed name.
                    onToggle={() => {
                      if (comment.byAuthor) {
                        patch(comment.id, { byAuthor: false })
                        return
                      }
                      const creatorHandle =
                        typeof postAuthor === 'string' && normalizeHandle(postAuthor).length > 0
                          ? { username: postAuthor }
                          : {}
                      patch(comment.id, { byAuthor: true, ...creatorHandle })
                    }}
                  />
                )}
                {capabilities.pinned && depth === 0 && (
                  <TogglePill
                    pressed={comment.pinned}
                    label="Pinned"
                    ariaLabel={`Toggle pinned on comment by ${who}`}
                    onToggle={() => patch(comment.id, { pinned: !comment.pinned })}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        {comment.replies.length > 0 && (
          <div className="space-y-2 px-2 pb-2">
            {comment.replies.map(reply => (
              <div key={reply.id}>{renderCommentFields(reply, depth + 1, 'Reply')}</div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {comments.map((comment, index) => (
        <div key={comment.id}>{renderCommentFields(comment, 0, `Comment ${index + 1}`)}</div>
      ))}
      <button
        type="button"
        onClick={addTopLevel}
        disabled={atCapacity}
        className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-white py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
      >
        <Plus aria-hidden="true" className="h-3 w-3" />
        Add comment
      </button>
      {atCapacity && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400" role="status">
          Thread limit reached ({MAX_THREAD_COMMENTS} comments) — remove a comment to add another.
        </p>
      )}
    </div>
  )
}
